import { Button, Kind } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import {
  OrderingErrorCategory,
  useGetOrderingErrorConfigQuery,
} from 'api/orderingErrorConfig'
import InfoBox from 'components/InfoBox'
import Spinner from 'components/Spinner'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { useOrderingErrorMessages } from 'hooks/useOrderingErrorMessages'
import { buildTranslate } from 'locales'
import { useEffect } from 'react'
import CmsPage from 'scenes/Cms/components/CmsPage'
import AiGenerationModal from './AiGenerationModal'
import AiSuggestionsBanner from './AiSuggestionsBanner'
import ErrorCategoryCard from './ErrorCategoryCard'
import PublishBanner from './PublishBanner'

const t = buildTranslate('ordering_management.error_messages')

// Exhaustive list of providers with bespoke error-message copy. Add a new key
// here when a new provider starts surfacing provider-specific errors; an
// unknown provider falls back to t('provider_fallback').
type BespokeProvider = 'toast' | 'olo'
const PROVIDER_DISPLAY: Record<BespokeProvider, string> = {
  toast: 'Toast',
  olo: 'Olo',
}

const isBespokeProvider = (key: string): key is BespokeProvider =>
  key in PROVIDER_DISPLAY

type Props = {
  showTitle?: boolean
}

const ErrorMessages = ({ showTitle }: Props) => {
  const [css] = useStyletron()
  const merchant = useCurrentMerchant()
  const providerKey = merchant?.ordering_provider ?? ''
  const providerName = isBespokeProvider(providerKey)
    ? PROVIDER_DISPLAY[providerKey]
    : t('provider_fallback')

  const {
    hasPendingSuggestions,
    pendingSuggestionsCount,
    initialize,
    openAiModal,
    acceptAllSuggestions,
    rejectAllSuggestions,
    handlePerEntryAi,
    handlePublish,
    isSaving,
  } = useOrderingErrorMessages()

  const {
    data,
    isLoading: isLoadingConfig,
    isError: loadError,
  } = useGetOrderingErrorConfigQuery()

  // Initialize customizations from server data
  useEffect(() => {
    if (data?.categories) {
      initialize(data.categories, data.brand_tone)
    }
  }, [data, initialize])

  if (isLoadingConfig) {
    return (
      <div className="text-center py-xxl" data-testid="spinner">
        <Spinner />
      </div>
    )
  }

  if (loadError || !data) {
    return (
      <InfoBox
        type="alert"
        title={t('load_error_title')}
        content={t('load_error_content')}
        isClosable={false}
      />
    )
  }

  const categories = data.categories

  return (
    <CmsPage title={t('title')} showTitle={showTitle}>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        })}
      >
        {/* Section header + description */}
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          })}
        >
          <Text.Header3 className="m-0">
            {t('section_header', { provider: providerName })}
          </Text.Header3>
          <Text.BodyText3 color="grey70" className="m-0 d-block">
            {t('subtitle', { provider: providerName })}
          </Text.BodyText3>
        </div>

        {/* Publish banner - top */}
        <PublishBanner isSaving={isSaving} onPublish={handlePublish} />

        {/* Accept/reject all AI suggestions banner */}
        {hasPendingSuggestions && (
          <AiSuggestionsBanner
            count={pendingSuggestionsCount}
            onAcceptAll={acceptAllSuggestions}
            onRejectAll={rejectAllSuggestions}
          />
        )}

        {/* Hint + Generate button */}
        <div
          className={`px-m ${css({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          })}`}
        >
          <Text.BodyText3 tag="span" color="grey70">
            {t('default_message_hint', { provider: providerName })}
          </Text.BodyText3>
          <Button kind={Kind.SECONDARY} onClick={openAiModal}>
            {t('generate_with_ai')}
          </Button>
        </div>

        {/* AI Generation Modal */}
        <AiGenerationModal />

        {/* Error category cards */}
        <div>
          {categories.map((category: OrderingErrorCategory) => (
            <ErrorCategoryCard
              key={category.id}
              category={category}
              providerName={providerName}
              providerKey={providerKey}
              onGenerateAi={() => handlePerEntryAi(category.id)}
            />
          ))}
        </div>

        {/* Publish banner - bottom */}
        <PublishBanner isSaving={isSaving} onPublish={handlePublish} />
      </div>
    </CmsPage>
  )
}

export default ErrorMessages
